import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';

const AdminPanel = ({ auth }) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [comments, setComments] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: '', id: '' });
  const [userData, setUserData] = useState({ email: '', password: '', name: '', role: 'employee' });
  const [categoryData, setCategoryData] = useState({ name: '', description: '' });
  const [editUserOpen, setEditUserOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editCategoryData, setEditCategoryData] = useState({ name: '', description: '' });
  
  // Directory state
  const [staff, setStaff] = useState([]);
  const [tradesmen, setTradesmen] = useState([]);
  const [otherContacts, setOtherContacts] = useState([]);
  const [staffDialogOpen, setStaffDialogOpen] = useState(false);
  const [tradesmanDialogOpen, setTradesmanDialogOpen] = useState(false);
  const [otherContactDialogOpen, setOtherContactDialogOpen] = useState(false);
  const [staffData, setStaffData] = useState({ full_name: '', phone: '', email: '' });
  const [tradesmanData, setTradesmanData] = useState({ business_name: '', trade_type: '', contact_number: '', email: '' });
  const [otherContactData, setOtherContactData] = useState({ business_name: '', what_they_do: '', name: '', contact_number: '', email: '' });
  const [activeTab, setActiveTab] = useState('pending');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, catsRes, docsRes, commentsRes, notificationsRes, logsRes, staffRes, tradesmenRes, contactsRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/documents`),
        axios.get(`${API}/comments`),
        axios.get(`${API}/notifications`),
        axios.get(`${API}/activity-logs`),
        axios.get(`${API}/directory/staff`),
        axios.get(`${API}/directory/tradesmen`),
        axios.get(`${API}/directory/other-contacts`)
      ]);
      setUsers(usersRes.data);
      setCategories(catsRes.data);
      setDocuments(docsRes.data);
      setComments(commentsRes.data);
      setNotifications(notificationsRes.data);
      setActivityLogs(logsRes.data);
      setStaff(staffRes.data);
      setTradesmen(tradesmenRes.data);
      setOtherContacts(contactsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/users`, userData);
      toast.success('User created successfully');
      setUserDialogOpen(false);
      setUserData({ email: '', password: '', name: '', role: 'employee' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleDeleteUser = async () => {
    try {
      await axios.delete(`${API}/users/${deleteDialog.id}`);
      toast.success('User deleted');
      setDeleteDialog({ open: false, type: '', id: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const handleApproveUser = async (userId) => {
    try {
      await axios.patch(`${API}/users/${userId}/approve`);
      toast.success('User approved! They can now log in.');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve user');
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${API}/users/${editingUser.id}`, {
        name: editingUser.name,
        email: editingUser.email,
        role: editingUser.role
      });
      toast.success('User updated successfully');
      setEditUserOpen(false);
      setEditingUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category.id);
    setEditCategoryData({ name: category.name, description: category.description });
  };

  const handleUpdateCategory = async (categoryId) => {
    try {
      await axios.patch(`${API}/categories/${categoryId}`, editCategoryData);
      toast.success('Category updated successfully');
      setEditingCategory(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update category');
    }
  };

  const handleCancelEditCategory = () => {
    setEditingCategory(null);
    setEditCategoryData({ name: '', description: '' });
  };

  // Directory handlers
  const handleCreateStaff = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/directory/staff`, staffData);
      toast.success('Staff member added');
      setStaffDialogOpen(false);
      setStaffData({ full_name: '', phone: '', email: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to add staff');
    }
  };

  const handleCreateTradesman = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/directory/tradesmen`, tradesmanData);
      toast.success('Tradesman added');
      setTradesmanDialogOpen(false);
      setTradesmanData({ business_name: '', trade_type: '', contact_number: '', email: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to add tradesman');
    }
  };

  const handleCreateOtherContact = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/directory/other-contacts`, otherContactData);
      toast.success('Contact added');
      setOtherContactDialogOpen(false);
      setOtherContactData({ business_name: '', what_they_do: '', name: '', contact_number: '', email: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to add contact');
    }
  };

  const handleDeleteStaff = async () => {
    try {
      await axios.delete(`${API}/directory/staff/${deleteDialog.id}`);
      toast.success('Staff deleted');
      setDeleteDialog({ open: false, type: '', id: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to delete staff');
    }
  };

  const handleDeleteTradesman = async () => {
    try {
      await axios.delete(`${API}/directory/tradesmen/${deleteDialog.id}`);
      toast.success('Tradesman deleted');
      setDeleteDialog({ open: false, type: '', id: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to delete tradesman');
    }
  };

  const handleDeleteContact = async () => {
    try {
      await axios.delete(`${API}/directory/other-contacts/${deleteDialog.id}`);
      toast.success('Contact deleted');
      setDeleteDialog({ open: false, type: '', id: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to delete contact');
    }
  };

  const handleCopyEmail = (email) => {
    navigator.clipboard.writeText(email).then(() => {
      toast.success('Email copied to clipboard!');
    }).catch(() => {
      toast.error('Failed to copy email');
    });
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/categories`, categoryData);
      toast.success('Category created');
      setCategoryDialogOpen(false);
      setCategoryData({ name: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create category');
    }
  };

  const handleDeleteCategory = async () => {
    try {
      await axios.delete(`${API}/categories/${deleteDialog.id}`);
      toast.success('Category deleted');
      setDeleteDialog({ open: false, type: '', id: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  const handleApproveDocument = async (docId) => {
    try {
      await axios.patch(`${API}/documents/${docId}/approve`);
      toast.success('Document approved');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve document');
    }
  };

  const handleDeleteDocument = async () => {
    try {
      await axios.delete(`${API}/documents/${deleteDialog.id}`);
      toast.success('Document deleted');
      setDeleteDialog({ open: false, type: '', id: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const handleToggleFlipbook = async (docId) => {
    try {
      const response = await axios.patch(`${API}/flipbooks/${docId}/toggle`);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle flipbook status');
    }
  };

  const handleCompleteComment = async (commentId) => {
    try {
      await axios.patch(`${API}/comments/${commentId}/complete`);
      toast.success('Comment marked as completed');
      fetchData();
    } catch (error) {
      toast.error('Failed to complete comment');
    }
  };

  const handleApproveStaff = async (staffId) => {
    try {
      await axios.patch(`${API}/directory/staff/${staffId}/approve`);
      toast.success('Staff member approved');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve staff');
    }
  };

  const handleApproveTradesman = async (tradesmanId) => {
    try {
      await axios.patch(`${API}/directory/tradesmen/${tradesmanId}/approve`);
      toast.success('Tradesman approved');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve tradesman');
    }
  };

  const handleApproveContact = async (contactId) => {
    try {
      await axios.patch(`${API}/directory/other-contacts/${contactId}/approve`);
      toast.success('Contact approved');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve contact');
    }
  };

  const pendingDocs = documents.filter(doc => !doc.approved);
  const pendingComments = comments.filter(c => c.status === 'pending');
  const completedComments = comments.filter(c => c.status === 'completed');
  const pendingUsers = users.filter(u => u.status === 'pending');
  const activeUsers = users.filter(u => u.status === 'active');
  const pendingStaff = staff.filter(s => !s.approved);
  const approvedStaff = staff.filter(s => s.approved);
  const pendingTradesmen = tradesmen.filter(t => !t.approved);
  const approvedTradesmen = tradesmen.filter(t => t.approved);
  const pendingContacts = otherContacts.filter(c => !c.approved);
  const approvedContacts = otherContacts.filter(c => c.approved);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E4E1DC]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#E4E1DC] via-white to-[#E4E1DC]">
      {/* Header */}
      <header className="bg-[#E4E1DC] border-b border-[#E4E1DC] shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img src="/avenue-logo-purple.svg" alt="Avenue" className="h-10 sm:h-9 w-auto" />
              <div className="hidden sm:block border-l border-[#21123B]/20 pl-4">
                <h1 className="text-lg font-bold heading text-[#21123B]">Admin Panel</h1>
                <p className="text-xs text-[#21123B]/70">Manage users, documents & categories</p>
              </div>
            </div>
            <Button
              onClick={() => navigate('/')}
              className="bg-[#21123B] text-[#E4E1DC] border-0 hover:bg-[#21123B]/90"
              data-testid="back-to-dashboard-button"
            >
              Back to Dashboard
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          {/* Mobile Dropdown Navigation */}
          <div className="block lg:hidden mb-6">
            <Select value={activeTab} onValueChange={setActiveTab}>
              <SelectTrigger className="w-full" style={{ borderColor: '#E4E1DC' }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">
                  Pending Documents ({pendingDocs.length})
                </SelectItem>
                <SelectItem value="users">Users</SelectItem>
                <SelectItem value="categories">Categories</SelectItem>
                <SelectItem value="comments">
                  Comments ({pendingComments.length})
                </SelectItem>
                <SelectItem value="directory">Directory</SelectItem>
                <SelectItem value="activity">Activity Log</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Desktop Tab Navigation */}
          <TabsList className="hidden lg:flex w-full lg:w-auto">
            <TabsTrigger value="pending" data-testid="tab-pending">
              Pending ({pendingDocs.length})
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
            <TabsTrigger value="categories" data-testid="tab-categories">Categories</TabsTrigger>
            <TabsTrigger value="comments" data-testid="tab-comments">
              Comments ({pendingComments.length})
            </TabsTrigger>
            <TabsTrigger value="directory" data-testid="tab-directory">Directory</TabsTrigger>
            <TabsTrigger value="activity" data-testid="tab-activity">Activity Log</TabsTrigger>
          </TabsList>

          {/* Pending Documents */}
          <TabsContent value="pending" className="space-y-4">
            {pendingDocs.length === 0 ? (
              <Card>
                <CardContent className="pt-6 text-center text-gray-500">
                  No pending documents
                </CardContent>
              </Card>
            ) : (
              pendingDocs.map(doc => (
                <Card key={doc.id} data-testid={`pending-doc-${doc.id}`}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle data-testid="pending-doc-title">{doc.title}</CardTitle>
                        <CardDescription data-testid="pending-doc-description">{doc.description}</CardDescription>
                      </div>
                      <Badge className="bg-yellow-100 text-yellow-700">Pending Approval</Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Category:</span> <span className="font-medium">{doc.category_name}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Type:</span> <span className="font-medium">{doc.doc_type}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Uploaded by:</span> <span className="font-medium">{doc.uploaded_by_name}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Date:</span> <span className="font-medium">{new Date(doc.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        onClick={() => handleApproveDocument(doc.id)}
                        style={{ backgroundColor: '#21123B', color: '#E4E1DC' }}
                        data-testid="approve-doc-button"
                      >
                        Approve
                      </Button>
                      <Button
                        onClick={() => setDeleteDialog({ open: true, type: 'document', id: doc.id })}
                        variant="destructive"
                        data-testid="reject-doc-button"
                      >
                        Reject
                      </Button>
                      {doc.doc_type === 'pdf' && (
                        <Button
                          onClick={() => handleToggleFlipbook(doc.id)}
                          variant="outline"
                          className="border-[#21123B]/40 text-[#21123B]"
                        >
                          {doc.is_flipbook ? '📖 Flipbook Enabled' : '📄 Enable Flipbook'}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Users */}
          <TabsContent value="users" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold heading" style={{ color: '#2F323B' }}>Users</h2>
              <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
                <DialogTrigger asChild>
                  <Button style={{ backgroundColor: '#21123B', color: '#E4E1DC' }} data-testid="add-user-button">
                    Add User
                  </Button>
                </DialogTrigger>
                <DialogContent data-testid="add-user-dialog">
                  <DialogHeader>
                    <DialogTitle>Create New User</DialogTitle>
                    <DialogDescription>Add a new employee or admin</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateUser} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Name</Label>
                      <Input
                        value={userData.name}
                        onChange={(e) => setUserData({ ...userData, name: e.target.value })}
                        required
                        data-testid="user-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={userData.email}
                        onChange={(e) => setUserData({ ...userData, email: e.target.value })}
                        required
                        data-testid="user-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Password</Label>
                      <Input
                        type="password"
                        value={userData.password}
                        onChange={(e) => setUserData({ ...userData, password: e.target.value })}
                        required
                        data-testid="user-password-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Role</Label>
                      <select
                        className="w-full border rounded-md px-3 py-2"
                        value={userData.role}
                        onChange={(e) => setUserData({ ...userData, role: e.target.value })}
                        data-testid="user-role-select"
                      >
                        <option value="employee">Employee</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                    <Button type="submit" className="w-full" style={{ backgroundColor: '#21123B', color: '#E4E1DC' }} data-testid="create-user-button">
                      Create User
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            
            {pendingUsers.length > 0 && (
              <>
                <h3 className="text-lg font-semibold text-orange-600 mt-6">Pending Approval ({pendingUsers.length})</h3>
                <div className="grid gap-4">
                  {pendingUsers.map(user => (
                    <Card key={user.id} data-testid={`pending-user-${user.id}`} className="border-l-4 border-l-orange-500">
                      <CardContent className="flex items-center justify-between pt-6">
                        <div>
                          <h3 className="font-semibold" data-testid="user-name">{user.name}</h3>
                          <p className="text-sm text-gray-600" data-testid="user-email">{user.email}</p>
                          <Badge className="mt-2 bg-orange-100 text-orange-700">
                            Pending Approval
                          </Badge>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleApproveUser(user.id)}
                            style={{ backgroundColor: '#21123B', color: '#E4E1DC' }}
                            size="sm"
                            data-testid="approve-user-button"
                          >
                            Approve
                          </Button>
                          <Button
                            onClick={() => setDeleteDialog({ open: true, type: 'user', id: user.id })}
                            variant="destructive"
                            size="sm"
                            data-testid="reject-user-button"
                          >
                            Reject
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </>
            )}
            
            <h3 className="text-lg font-semibold mt-6">Active Users ({activeUsers.length})</h3>
            <div className="grid gap-4">
              {activeUsers.map(user => (
                <Card key={user.id} data-testid={`user-${user.id}`}>
                  <CardContent className="flex items-center justify-between pt-6">
                    <div>
                      <h3 className="font-semibold" data-testid="user-name">{user.name}</h3>
                      <p className="text-sm text-gray-600" data-testid="user-email">{user.email}</p>
                      <Badge className="mt-2" variant={user.role === 'admin' ? 'default' : 'secondary'}>
                        {user.role}
                      </Badge>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => {
                          setEditingUser(user);
                          setEditUserOpen(true);
                        }}
                        variant="outline"
                        size="sm"
                        data-testid="edit-user-button"
                      >
                        Edit
                      </Button>
                      {user.id !== auth.user.id && (
                        <Button
                          onClick={() => setDeleteDialog({ open: true, type: 'user', id: user.id })}
                          variant="destructive"
                          size="sm"
                          data-testid="delete-user-button"
                        >
                          Delete
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            {/* Edit User Dialog */}
            <Dialog open={editUserOpen} onOpenChange={setEditUserOpen}>
              <DialogContent data-testid="edit-user-dialog">
                <DialogHeader>
                  <DialogTitle>Edit User</DialogTitle>
                  <DialogDescription>Update user details and access level</DialogDescription>
                </DialogHeader>
                {editingUser && (
                  <form onSubmit={handleEditUser} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Name</Label>
                      <Input
                        value={editingUser.name}
                        onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                        required
                        data-testid="edit-user-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={editingUser.email}
                        onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                        required
                        data-testid="edit-user-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Role</Label>
                      <select
                        className="w-full border rounded-md px-3 py-2"
                        value={editingUser.role}
                        onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                        data-testid="edit-user-role-select"
                      >
                        <option value="employee">Employee</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                    <Button type="submit" className="w-full" style={{ backgroundColor: '#21123B', color: '#E4E1DC' }} data-testid="update-user-button">
                      Update User
                    </Button>
                  </form>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Categories */}
          <TabsContent value="categories" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold heading" style={{ color: '#2F323B' }}>Categories</h2>
              <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
                <DialogTrigger asChild>
                  <Button style={{ backgroundColor: '#21123B', color: '#E4E1DC' }} data-testid="add-category-button">
                    Add Category
                  </Button>
                </DialogTrigger>
                <DialogContent data-testid="add-category-dialog">
                  <DialogHeader>
                    <DialogTitle>Create New Category</DialogTitle>
                    <DialogDescription>Organize documents by category</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateCategory} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Name</Label>
                      <Input
                        value={categoryData.name}
                        onChange={(e) => setCategoryData({ ...categoryData, name: e.target.value })}
                        required
                        data-testid="category-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Textarea
                        value={categoryData.description}
                        onChange={(e) => setCategoryData({ ...categoryData, description: e.target.value })}
                        data-testid="category-description-input"
                      />
                    </div>
                    <Button type="submit" className="w-full" style={{ backgroundColor: '#21123B', color: '#E4E1DC' }} data-testid="create-category-button">
                      Create Category
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            <div className="grid gap-4">
              {categories.map(cat => (
                <Card key={cat.id} data-testid={`category-${cat.id}`}>
                  <CardContent className="flex items-center justify-between pt-6">
                    {editingCategory === cat.id ? (
                      <div className="flex-1 space-y-2">
                        <Input
                          value={editCategoryData.name}
                          onChange={(e) => setEditCategoryData({ ...editCategoryData, name: e.target.value })}
                          placeholder="Category name"
                          className="font-semibold"
                        />
                        <Input
                          value={editCategoryData.description}
                          onChange={(e) => setEditCategoryData({ ...editCategoryData, description: e.target.value })}
                          placeholder="Description"
                          className="text-sm"
                        />
                        <div className="flex gap-2 pt-2">
                          <Button
                            onClick={() => handleUpdateCategory(cat.id)}
                            size="sm"
                            style={{ backgroundColor: '#21123B', color: '#E4E1DC' }}
                          >
                            Save
                          </Button>
                          <Button
                            onClick={handleCancelEditCategory}
                            variant="outline"
                            size="sm"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div>
                          <h3 className="font-semibold" data-testid="category-name">{cat.name}</h3>
                          <p className="text-sm text-gray-600" data-testid="category-description">{cat.description}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleEditCategory(cat)}
                            variant="outline"
                            size="sm"
                          >
                            Edit
                          </Button>
                          <Button
                            onClick={() => setDeleteDialog({ open: true, type: 'category', id: cat.id })}
                            variant="destructive"
                            size="sm"
                            data-testid="delete-category-button"
                          >
                            Delete
                          </Button>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Comments */}
          <TabsContent value="comments" className="space-y-4">
            <h3 className="text-lg font-semibold">Pending Comments ({pendingComments.length})</h3>
            {pendingComments.length === 0 ? (
              <Card>
                <CardContent className="pt-6 text-center text-gray-500">
                  No pending comments
                </CardContent>
              </Card>
            ) : (
              pendingComments.map(comment => (
                <Card key={comment.id} data-testid={`comment-${comment.id}`} className="border-l-4 border-l-orange-500">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-base flex items-center gap-2" data-testid="comment-user">
                          {comment.user_name}
                          <Badge className="bg-orange-100 text-orange-700">Pending</Badge>
                        </CardTitle>
                        <CardDescription>
                          Document: {comment.document_title || 'Unknown'} • {new Date(comment.created_at).toLocaleString()}
                        </CardDescription>
                      </div>
                      <Button
                        onClick={() => handleCompleteComment(comment.id)}
                        size="sm"
                        style={{ backgroundColor: '#21123B', color: '#E4E1DC' }}
                        data-testid="complete-comment-button"
                      >
                        Mark Completed
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm" data-testid="comment-message">{comment.message}</p>
                  </CardContent>
                </Card>
              ))
            )}
            
            {completedComments.length > 0 && (
              <>
                <h3 className="text-lg font-semibold mt-8">Completed Comments ({completedComments.length})</h3>
                {completedComments.map(comment => (
                  <Card key={comment.id} className="opacity-60">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-base flex items-center gap-2">
                            {comment.user_name}
                            <Badge variant="outline" className="border-green-400 text-green-700">Completed</Badge>
                          </CardTitle>
                          <CardDescription>
                            Document: {comment.document_title || 'Unknown'} • {new Date(comment.created_at).toLocaleString()}
                          </CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm">{comment.message}</p>
                    </CardContent>
                  </Card>
                ))}
              </>
            )}
          </TabsContent>

          {/* Directory */}
          <TabsContent value="directory" className="space-y-6">
            <h2 className="text-2xl font-bold heading" style={{ color: '#2F323B' }}>Directory</h2>
            
            {/* Staff Section */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Staff ({staff.length})</h3>
                <Dialog open={staffDialogOpen} onOpenChange={setStaffDialogOpen}>
                  <DialogTrigger asChild>
                    <Button style={{ backgroundColor: '#21123B', color: '#E4E1DC' }} size="sm">Add Staff</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add Staff Member</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateStaff} className="space-y-4">
                      <div className="space-y-2">
                        <Label>Full Name</Label>
                        <Input value={staffData.full_name} onChange={(e) => setStaffData({ ...staffData, full_name: e.target.value })} required />
                      </div>
                      <div className="space-y-2">
                        <Label>Phone</Label>
                        <Input value={staffData.phone} onChange={(e) => setStaffData({ ...staffData, phone: e.target.value })} required />
                      </div>
                      <div className="space-y-2">
                        <Label>Email</Label>
                        <Input type="email" value={staffData.email} onChange={(e) => setStaffData({ ...staffData, email: e.target.value })} required />
                      </div>
                      <Button type="submit" className="w-full" style={{ backgroundColor: '#21123B', color: '#E4E1DC' }}>Add Staff</Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
              
              {/* Pending Staff */}
              {pendingStaff.length > 0 && (
                <>
                  <h4 className="text-md font-semibold text-orange-600 mb-3">Pending Approval ({pendingStaff.length})</h4>
                  <div className="grid gap-3 mb-4">
                    {pendingStaff.map(s => (
                      <Card key={s.id} className="border-l-4 border-l-orange-500">
                        <CardContent className="flex items-center justify-between pt-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold text-lg">{s.full_name}</h4>
                              <Badge className="bg-orange-100 text-orange-700">Pending</Badge>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">Added by: {s.uploaded_by_name}</p>
                            <div className="mt-2 space-y-1">
                              <div className="flex items-center gap-2 text-sm">
                                <span className="text-gray-600">📞</span>
                                <a href={`tel:${s.phone}`} className="text-[#21123B] hover:underline font-medium">
                                  {s.phone}
                                </a>
                              </div>
                              <div className="flex items-center gap-2 text-sm">
                                <span className="text-gray-600">📧</span>
                                <span className="text-gray-600">{s.email}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button onClick={() => handleApproveStaff(s.id)} style={{ backgroundColor: '#21123B', color: '#E4E1DC' }} size="sm">
                              Approve
                            </Button>
                            <Button onClick={() => setDeleteDialog({ open: true, type: 'staff', id: s.id })} variant="destructive" size="sm">Reject</Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                  <h4 className="text-md font-semibold mb-3">Approved Staff ({approvedStaff.length})</h4>
                </>
              )}
              
              <div className="grid gap-3">
                {approvedStaff.map(s => (
                  <Card key={s.id}>
                    <CardContent className="flex items-center justify-between pt-4">
                      <div className="flex-1">
                        <h4 className="font-semibold text-lg">{s.full_name}</h4>
                        <div className="mt-2 space-y-1">
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-gray-600">📞</span>
                            <a href={`tel:${s.phone}`} className="text-[#21123B] hover:underline font-medium">
                              {s.phone}
                            </a>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-gray-600">📧</span>
                            <span className="text-gray-600 flex-1">{s.email}</span>
                            <Button
                              onClick={() => handleCopyEmail(s.email)}
                              variant="outline"
                              size="sm"
                              className="h-7 px-2 ml-2"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </Button>
                          </div>
                        </div>
                      </div>
                      <Button onClick={() => setDeleteDialog({ open: true, type: 'staff', id: s.id })} variant="destructive" size="sm">Delete</Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Tradesmen Section */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Tradesmen ({tradesmen.length})</h3>
                <Dialog open={tradesmanDialogOpen} onOpenChange={setTradesmanDialogOpen}>
                  <DialogTrigger asChild>
                    <Button style={{ backgroundColor: '#21123B', color: '#E4E1DC' }} size="sm">Add Tradesman</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add Tradesman</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateTradesman} className="space-y-4">
                      <div className="space-y-2">
                        <Label>Business Name</Label>
                        <Input value={tradesmanData.business_name} onChange={(e) => setTradesmanData({ ...tradesmanData, business_name: e.target.value })} required />
                      </div>
                      <div className="space-y-2">
                        <Label>Trade Type</Label>
                        <Input value={tradesmanData.trade_type} onChange={(e) => setTradesmanData({ ...tradesmanData, trade_type: e.target.value })} required />
                      </div>
                      <div className="space-y-2">
                        <Label>Best Contact Number</Label>
                        <Input value={tradesmanData.contact_number} onChange={(e) => setTradesmanData({ ...tradesmanData, contact_number: e.target.value })} required />
                      </div>
                      <div className="space-y-2">
                        <Label>Email</Label>
                        <Input type="email" value={tradesmanData.email} onChange={(e) => setTradesmanData({ ...tradesmanData, email: e.target.value })} required />
                      </div>
                      <Button type="submit" className="w-full" style={{ backgroundColor: '#21123B', color: '#E4E1DC' }}>Add Tradesman</Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
              
              {/* Pending Tradesmen */}
              {pendingTradesmen.length > 0 && (
                <>
                  <h4 className="text-md font-semibold text-orange-600 mb-3">Pending Approval ({pendingTradesmen.length})</h4>
                  <div className="grid gap-3 mb-4">
                    {pendingTradesmen.map(t => (
                      <Card key={t.id} className="border-l-4 border-l-orange-500">
                        <CardContent className="flex items-center justify-between pt-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold text-lg">{t.business_name}</h4>
                              <Badge className="bg-orange-100 text-orange-700">Pending</Badge>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">Added by: {t.uploaded_by_name}</p>
                            <p className="text-sm text-gray-600 mt-1">Trade: {t.trade_type}</p>
                            <div className="mt-2 space-y-1">
                              <div className="flex items-center gap-2 text-sm">
                                <span className="text-gray-600">📞</span>
                                <a href={`tel:${t.contact_number}`} className="text-[#21123B] hover:underline font-medium">
                                  {t.contact_number}
                                </a>
                              </div>
                              <div className="flex items-center gap-2 text-sm">
                                <span className="text-gray-600">📧</span>
                                <span className="text-gray-600">{t.email}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button onClick={() => handleApproveTradesman(t.id)} style={{ backgroundColor: '#21123B', color: '#E4E1DC' }} size="sm">
                              Approve
                            </Button>
                            <Button onClick={() => setDeleteDialog({ open: true, type: 'tradesman', id: t.id })} variant="destructive" size="sm">Reject</Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                  <h4 className="text-md font-semibold mb-3">Approved Tradesmen ({approvedTradesmen.length})</h4>
                </>
              )}
              
              <div className="grid gap-3">
                {approvedTradesmen.map(t => (
                  <Card key={t.id}>
                    <CardContent className="flex items-center justify-between pt-4">
                      <div className="flex-1">
                        <h4 className="font-semibold text-lg">{t.business_name}</h4>
                        <p className="text-sm text-gray-600 mt-1">Trade: {t.trade_type}</p>
                        <div className="mt-2 space-y-1">
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-gray-600">📞</span>
                            <a href={`tel:${t.contact_number}`} className="text-[#21123B] hover:underline font-medium">
                              {t.contact_number}
                            </a>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-gray-600">📧</span>
                            <span className="text-gray-600 flex-1">{t.email}</span>
                            <Button
                              onClick={() => handleCopyEmail(t.email)}
                              variant="outline"
                              size="sm"
                              className="h-7 px-2 ml-2"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </Button>
                          </div>
                        </div>
                      </div>
                      <Button onClick={() => setDeleteDialog({ open: true, type: 'tradesman', id: t.id })} variant="destructive" size="sm">Delete</Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Other Contacts Section */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Other Contacts ({otherContacts.length})</h3>
                <Dialog open={otherContactDialogOpen} onOpenChange={setOtherContactDialogOpen}>
                  <DialogTrigger asChild>
                    <Button style={{ backgroundColor: '#21123B', color: '#E4E1DC' }} size="sm">Add Contact</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add Other Contact</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateOtherContact} className="space-y-4">
                      <div className="space-y-2">
                        <Label>Business Name</Label>
                        <Input value={otherContactData.business_name} onChange={(e) => setOtherContactData({ ...otherContactData, business_name: e.target.value })} required />
                      </div>
                      <div className="space-y-2">
                        <Label>What They Do</Label>
                        <Input value={otherContactData.what_they_do} onChange={(e) => setOtherContactData({ ...otherContactData, what_they_do: e.target.value })} required />
                      </div>
                      <div className="space-y-2">
                        <Label>Contact Name</Label>
                        <Input value={otherContactData.name} onChange={(e) => setOtherContactData({ ...otherContactData, name: e.target.value })} required />
                      </div>
                      <div className="space-y-2">
                        <Label>Contact Number</Label>
                        <Input value={otherContactData.contact_number} onChange={(e) => setOtherContactData({ ...otherContactData, contact_number: e.target.value })} required />
                      </div>
                      <div className="space-y-2">
                        <Label>Email</Label>
                        <Input type="email" value={otherContactData.email} onChange={(e) => setOtherContactData({ ...otherContactData, email: e.target.value })} required />
                      </div>
                      <Button type="submit" className="w-full" style={{ backgroundColor: '#21123B', color: '#E4E1DC' }}>Add Contact</Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
              
              {/* Pending Other Contacts */}
              {pendingContacts.length > 0 && (
                <>
                  <h4 className="text-md font-semibold text-orange-600 mb-3">Pending Approval ({pendingContacts.length})</h4>
                  <div className="grid gap-3 mb-4">
                    {pendingContacts.map(c => (
                      <Card key={c.id} className="border-l-4 border-l-orange-500">
                        <CardContent className="flex items-center justify-between pt-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold text-lg">{c.business_name}</h4>
                              <Badge className="bg-orange-100 text-orange-700">Pending</Badge>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">Added by: {c.uploaded_by_name}</p>
                            <p className="text-sm text-gray-600 mt-1">{c.what_they_do}</p>
                            <p className="text-sm text-gray-600">Contact: {c.name}</p>
                            <div className="mt-2 space-y-1">
                              <div className="flex items-center gap-2 text-sm">
                                <span className="text-gray-600">📞</span>
                                <a href={`tel:${c.contact_number}`} className="text-[#21123B] hover:underline font-medium">
                                  {c.contact_number}
                                </a>
                              </div>
                              <div className="flex items-center gap-2 text-sm">
                                <span className="text-gray-600">📧</span>
                                <span className="text-gray-600">{c.email}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button onClick={() => handleApproveContact(c.id)} style={{ backgroundColor: '#21123B', color: '#E4E1DC' }} size="sm">
                              Approve
                            </Button>
                            <Button onClick={() => setDeleteDialog({ open: true, type: 'contact', id: c.id })} variant="destructive" size="sm">Reject</Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                  <h4 className="text-md font-semibold mb-3">Approved Contacts ({approvedContacts.length})</h4>
                </>
              )}
              
              <div className="grid gap-3">
                {approvedContacts.map(c => (
                  <Card key={c.id}>
                    <CardContent className="flex items-center justify-between pt-4">
                      <div className="flex-1">
                        <h4 className="font-semibold text-lg">{c.business_name}</h4>
                        <p className="text-sm text-gray-600 mt-1">{c.what_they_do}</p>
                        <p className="text-sm text-gray-600">Contact: {c.name}</p>
                        <div className="mt-2 space-y-1">
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-gray-600">📞</span>
                            <a href={`tel:${c.contact_number}`} className="text-[#21123B] hover:underline font-medium">
                              {c.contact_number}
                            </a>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-gray-600">📧</span>
                            <span className="text-gray-600 flex-1">{c.email}</span>
                            <Button
                              onClick={() => handleCopyEmail(c.email)}
                              variant="outline"
                              size="sm"
                              className="h-7 px-2 ml-2"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </Button>
                          </div>
                        </div>
                      </div>
                      <Button onClick={() => setDeleteDialog({ open: true, type: 'contact', id: c.id })} variant="destructive" size="sm">Delete</Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* Activity Log */}
          <TabsContent value="activity" className="space-y-4">
            <h2 className="text-2xl font-bold heading" style={{ color: '#2F323B' }}>Activity Log</h2>
            <p className="text-sm text-gray-600">Track all changes made by admins</p>
            {activityLogs.length === 0 ? (
              <Card>
                <CardContent className="pt-6 text-center text-gray-500">
                  No activity yet
                </CardContent>
              </Card>
            ) : (
              activityLogs.map(log => (
                <Card key={log.id} data-testid={`activity-${log.id}`}>
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge 
                            className={
                              log.action === 'document_approved' ? 'bg-green-100 text-green-700' :
                              log.action === 'document_deleted' ? 'bg-red-100 text-red-700' :
                              log.action === 'comment_completed' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-700'
                            }
                          >
                            {log.action.replace(/_/g, ' ').toUpperCase()}
                          </Badge>
                          <span className="text-xs text-gray-500">
                            {new Date(log.created_at).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-sm">{log.details}</p>
                        <p className="text-xs text-gray-500 mt-1">Admin: {log.admin_name}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, type: '', id: '' })}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete this {deleteDialog.type}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-button">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteDialog.type === 'user') handleDeleteUser();
                else if (deleteDialog.type === 'category') handleDeleteCategory();
                else if (deleteDialog.type === 'document') handleDeleteDocument();
                else if (deleteDialog.type === 'staff') handleDeleteStaff();
                else if (deleteDialog.type === 'tradesman') handleDeleteTradesman();
                else if (deleteDialog.type === 'contact') handleDeleteContact();
              }}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-button"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminPanel;
